import { Button, Flex, Group, Text, TextInput } from '@mantine/core';
import { RangeCalendar } from '@mantine/dates';
import { CalculateToPayResponse } from '../../../@types/bank-hour';
import { BalanceText } from '../../../components/balance-text';
import { User } from '../../../models/user';
import { parsePayHours } from '../../../utils/helpers';

interface PayBankFormProps {
  selectedDates: [Date | null, Date | null];
  setSelectedDates: React.Dispatch<
    React.SetStateAction<[Date | null, Date | null]>
  >;
  loading: boolean;
  handlePayBankHour: () => void;
  calculatedBankHours: CalculateToPayResponse[] | undefined;
  userInfo: User | undefined;
  /** HH:MM typed by the user; empty = pay the whole amount */
  payHours?: string;
  setPayHours?: (value: string) => void;
}

export function PayBankForm({
  selectedDates,
  setSelectedDates,
  loading,
  handlePayBankHour,
  calculatedBankHours,
  userInfo,
  payHours = '',
  setPayHours,
}: PayBankFormProps) {
  const hasPeriod = !!selectedDates[1];
  const calculated = calculatedBankHours?.[0];
  const payableOk = hasPeriod && !!calculated && calculated.totalTime > 0;

  const paySeconds = parsePayHours(payHours);
  const payError =
    payHours.trim() === '' || !calculated
      ? null
      : Number.isNaN(paySeconds)
      ? 'Use o formato HH:MM (ex: 7:00)'
      : (paySeconds as number) <= 0
      ? 'Informe um valor maior que zero'
      : (paySeconds as number) > calculated.totalTime
      ? `O valor passa do total a pagar (${calculated.totalTimeFormated})`
      : null;
  const canPay = payableOk && !payError;

  const hint = !hasPeriod
    ? 'Selecione o período que será pago.'
    : !calculated
    ? 'Não há horas a pagar neste período.'
    : calculated.totalTime <= 0
    ? 'O total do período é zero ou negativo, não há o que pagar.'
    : null;

  return (
    <Flex w="100%" direction="column" gap={18} justify="center">
      <Flex justify={'center'}>
        <RangeCalendar
          value={selectedDates}
          onChange={setSelectedDates}
          firstDayOfWeek="sunday"
          locale="pt-br"
        />
      </Flex>

      <TextInput
        disabled
        value={userInfo?.username ?? ''}
        label={'Funcionário'}
        description={'Funcionário que será pago'}
      />
      <Flex direction="column">
        <Text color="dimmed" size="sm">
          Horas a pagar
        </Text>
        {calculated ? (
          <BalanceText
            value={calculated.totalTime}
            formatted={calculated.totalTimeFormated}
          />
        ) : (
          <Text fw="bold">--</Text>
        )}
        <Text color="dimmed" size="xs" mt={4}>
          Soma das jornadas do período com os lançamentos manuais ainda não
          pagos.
        </Text>
      </Flex>
      {setPayHours && (
        <TextInput
          value={payHours}
          onChange={(e) => setPayHours(e.currentTarget.value)}
          label="Horas a pagar agora"
          description="Opcional. Em branco paga o total; o restante continua no banco."
          placeholder={
            calculated
              ? `ex: 7:00 (máx. ${calculated.totalTimeFormated})`
              : 'HH:MM'
          }
          disabled={!payableOk}
          error={payError}
        />
      )}
      {hint && (
        <Text color="orange" size="sm">
          {hint}
        </Text>
      )}

      <Group position="right">
        <Button
          loading={loading}
          disabled={!canPay}
          onClick={() => handlePayBankHour()}
        >
          Pagar horas
        </Button>
      </Group>
    </Flex>
  );
}
